"use client";

import { useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  readViewMode,
  writeViewMode,
  type CatalogViewMode,
} from "@/lib/catalog/view-mode";

export function ViewToggle({
  active,
  testId = "catalog-view-toggle",
  testIdPrefix = "catalog-view",
}: {
  active: CatalogViewMode;
  testId?: string;
  testIdPrefix?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // PASTE #393 C: the server now SSRs the saved view from the cookie, so for a
  // cookie-backed buyer `active` ALREADY equals their saved mode and this effect
  // no-ops - no router.replace, no flash. It only fires for a LEGACY buyer whose
  // pref is still localStorage-only (no cookie yet): persist the cookie so the
  // next load SSRs correctly, and switch this one (a single one-time migration).
  useEffect(() => {
    const urlMode = searchParams.get("view");
    if (urlMode) return; // URL is authoritative when present
    const saved = readViewMode();
    if (saved && saved !== active) {
      writeViewMode(saved); // migrate localStorage -> cookie for future loads
      const params = new URLSearchParams(searchParams);
      params.set("view", saved);
      router.replace(`${pathname}?${params.toString()}`);
    }
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setMode(mode: CatalogViewMode) {
    writeViewMode(mode);
    const params = new URLSearchParams(searchParams);
    params.set("view", mode);
    router.push(`${pathname}?${params.toString()}`);
  }

  return (
    <div
      data-testid={testId}
      role="group"
      aria-label="Catalog view"
      className="inline-flex rounded-full border border-brand-border bg-white p-1"
    >
      <button
        type="button"
        data-testid={`${testIdPrefix}-magazine`}
        aria-pressed={active === "magazine"}
        onClick={() => setMode("magazine")}
        className={
          active === "magazine"
            ? "rounded-full bg-brand-teal px-4 py-1.5 text-xs font-semibold text-white"
            : "rounded-full px-4 py-1.5 text-xs font-semibold text-brand-text-muted hover:text-brand-text"
        }
      >
        Magazine
      </button>
      <button
        type="button"
        data-testid={`${testIdPrefix}-power-buyer`}
        aria-pressed={active === "power-buyer"}
        onClick={() => setMode("power-buyer")}
        className={
          active === "power-buyer"
            ? "rounded-full bg-brand-teal px-4 py-1.5 text-xs font-semibold text-white"
            : "rounded-full px-4 py-1.5 text-xs font-semibold text-brand-text-muted hover:text-brand-text"
        }
      >
        Power Buyer
      </button>
    </div>
  );
}
